import React, { useEffect, useState, useRef } from 'react';
import { Send, Bot, User, Sparkles, FileText, DollarSign, CreditCard, HelpCircle, Car, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
interface Message {
  type: 'bot' | 'user';
  content: string;
  timestamp: Date;
}
export const AIAgent = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    type: 'bot',
    content: "Hello! I'm your Geldhaus AI Assistant. How can I help you today? I can assist with loan applications, checking loan limits, balance inquiries, car valuations, and answering any questions about our services.",
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (isExpanded) {
      scrollToBottom();
    }
  }, [messages, isExpanded]);
  const quickActions = [{
    icon: <FileText size={20} data-id="element-163" />,
    label: 'Apply for Loan',
    action: 'apply'
  }, {
    icon: <Car size={20} data-id="element-164" />,
    label: "What's My Car Worth?",
    action: 'car-value'
  }, {
    icon: <DollarSign size={20} data-id="element-165" />,
    label: 'Check Loan Limit',
    action: 'limit'
  }, {
    icon: <CreditCard size={20} data-id="element-166" />,
    label: 'Check Balance',
    action: 'balance'
  }, {
    icon: <HelpCircle size={20} data-id="element-167" />,
    label: 'General Inquiry',
    action: 'inquiry'
  }];
  const handleQuickAction = (action: string) => {
    let message = '';
    switch (action) {
      case 'apply':
        message = 'I would like to apply for a loan';
        break;
      case 'car-value':
        message = "What's my car worth? I want to check my loan limit";
        break;
      case 'limit':
        message = 'What is my loan limit?';
        break;
      case 'balance':
        message = 'What is my current loan balance?';
        break;
      case 'inquiry':
        message = 'I have a general question about your services';
        break;
    }
    handleSendMessage(message);
  };
  const callHuggingFaceAPI = async (userMessage: string, conversationHistory: Message[]) => {
    try {
      const response = await fetch('https://api-inference.huggingface.co/models/microsoft/DialoGPT-medium', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inputs: {
            past_user_inputs: conversationHistory.filter(m => m.type === 'user').map(m => m.content).slice(-5),
            generated_responses: conversationHistory.filter(m => m.type === 'bot').map(m => m.content).slice(-5),
            text: userMessage
          },
          parameters: {
            max_length: 200,
            temperature: 0.7,
            top_p: 0.9
          }
        })
      });
      if (!response.ok) {
        throw new Error('API request failed');
      }
      const data = await response.json();
      return data.generated_text || data.conversation?.generated_responses?.[0] || generateFallbackResponse(userMessage);
    } catch (error) {
      console.error('Hugging Face API error:', error);
      return generateFallbackResponse(userMessage);
    }
  };
  const generateFallbackResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    if (lowerMessage.includes('car worth') || lowerMessage.includes('car value') || lowerMessage.includes('valuation')) {
      return "I can help you determine your car's value! To provide an accurate estimate, I'll need:\n\n1. Make and Model (e.g., Toyota Corolla)\n2. Year of Manufacture\n3. Current Mileage\n4. General Condition (Excellent/Good/Fair)\n\nBased on this information, I can estimate your car's market value and calculate your potential loan limit (up to 70% of the vehicle's value). You can get up to KSh 3,000,000 depending on your car's value!";
    }
    if (lowerMessage.includes('loan') && (lowerMessage.includes('apply') || lowerMessage.includes('application'))) {
      return "Great! I'd be happy to help you apply for a loan. Geldhaus offers several loan products:\n\n• Fast Cash Logbook Loan (KSh 100K - 3M)\n• Secured Business Loan (KSh 500K - 5M)\n• Car & Asset Financing (KSh 500K - 10M)\n\nWhich product interests you? Or would you like me to recommend one based on your needs?";
    }
    if (lowerMessage.includes('interest rate') || lowerMessage.includes('rate')) {
      return 'Our interest rates are competitive and start from:\n\n• Logbook Loans: From 3.5% per month\n• Business Loans: From 2.5% per month\n• Asset Financing: From 12% per annum\n\nRates depend on your credit profile and loan amount. We also offer reducing balance calculations for lower overall interest. Would you like to use our loan calculator to see exact figures?';
    }
    if (lowerMessage.includes('requirement') || lowerMessage.includes('document') || lowerMessage.includes('need')) {
      return "To apply for a Geldhaus loan, you'll need:\n\n✓ Valid National ID\n✓ KRA PIN Certificate\n✓ Car Logbook (for logbook loans)\n✓ Proof of income/business registration\n✓ Recent bank statements (3 months)\n\nThe application process takes 1-12 hours for approval. Would you like to start your application now?";
    }
    if (lowerMessage.includes('time') || lowerMessage.includes('how long') || lowerMessage.includes('approval')) {
      return 'Geldhaus prides itself on fast processing! ⚡\n\n• Application Review: 1-12 hours\n• Approval Decision: Same day\n• Fund Disbursement: Within 24 hours of approval\n\nOur 90% approval rate means most applications are successful. Ready to apply?';
    }
    if (lowerMessage.includes('balance') || lowerMessage.includes('outstanding') || lowerMessage.includes('owe')) {
      return "To check your loan balance, I'll need to verify your account. Please sign in to your Geldhaus account, or contact our customer service at:\n\n📞 +254 100 254 254\n📧 info@geldhaus.africa\n💬 WhatsApp: Available 24/7\n\nThey'll provide your current balance, next payment date, and payment options.";
    }
    if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return "You're welcome! I'm here to help anytime. If you have more questions about our loan products, application process, or anything else, feel free to ask. Have a great day! 😊";
    }
    return "I'm here to help with information about Geldhaus loan products, application processes, requirements, and more. Could you please provide more details about what you'd like to know? You can ask about:\n\n• Loan products and rates\n• Application requirements\n• Car valuation\n• Repayment options\n• Contact information\n\nWhat would you like to know more about?";
  };
  const handleSendMessage = async (message?: string) => {
    const messageToSend = message || inputValue;
    if (!messageToSend.trim() || isLoading) return;
    const userMessage: Message = {
      type: 'user',
      content: messageToSend,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    try {
      const botResponse = await callHuggingFaceAPI(messageToSend, messages);
      const botMessage: Message = {
        type: 'bot',
        content: botResponse,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage: Message = {
        type: 'bot',
        content: "I apologize, but I'm having trouble processing your request right now. Please try again or contact our support team at +254 100 254 254.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  return <section id="ai-agent" className="py-12 bg-gradient-to-br from-primary-600 to-primary-700 text-white relative overflow-hidden" data-id="element-168">
      <div className="absolute inset-0 opacity-10" data-id="element-169">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-170"></div>
      </div>

      <div className="container relative z-10" data-id="element-171">
        {/* Collapsed Header */}
        <div className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)} role="button" tabIndex={0} aria-expanded={isExpanded} aria-label={isExpanded ? 'Collapse AI Assistant' : 'Expand AI Assistant'} onKeyPress={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsExpanded(!isExpanded);
        }
      }} data-id="element-172">
          <div className="flex items-center justify-between max-w-5xl mx-auto" data-id="element-173">
            <div className="flex items-center gap-4" data-id="element-174">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-175">
                <Bot size={32} data-id="element-176" />
              </div>
              <div data-id="element-177">
                <h2 className="text-3xl md:text-4xl font-bold" data-id="element-178">
                  Geldhaus AI Assistant
                </h2>
                <p className="text-primary-100 flex items-center gap-2 mt-1" data-id="element-179">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-180"></span>
                  Online • Instant Response • Available 24/7
                </p>
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 hover:bg-white/30 transition-colors duration-200" data-id="element-181">
              {isExpanded ? <ChevronUp size={28} data-id="element-182" /> : <ChevronDown size={28} data-id="element-183" />}
            </div>
          </div>
        </div>

        {/* Quick Actions - Always Visible */}
        <div className="mt-8 max-w-5xl mx-auto" data-id="element-184">
          <p className="text-sm text-primary-100 mb-4 font-semibold" data-id="element-185">
            Quick Actions:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-186">
            {quickActions.map((action, index) => <button key={index} onClick={() => {
            setIsExpanded(true);
            handleQuickAction(action.action);
          }} disabled={isLoading} className="flex flex-col items-center gap-3 p-5 bg-white/10 backdrop-blur-sm rounded-xl border-2 border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed" aria-label={action.label} data-id="element-187">
                <div className="text-white group-hover:scale-110 transition-transform duration-300" data-id="element-188">
                  {action.icon}
                </div>
                <span className="text-xs text-white text-center font-semibold" data-id="element-189">
                  {action.label}
                </span>
              </button>)}
          </div>
        </div>

        {/* Expanded Chat Interface */}
        {isExpanded && <div className="max-w-5xl mx-auto mt-8 animate-slideUp" data-id="element-190">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-191">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6" data-id="element-192">
                <div className="flex items-center justify-between" data-id="element-193">
                  <div className="flex items-center gap-4" data-id="element-194">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-195">
                      <Bot size={28} data-id="element-196" />
                    </div>
                    <div data-id="element-197">
                      <h3 className="font-bold text-lg" data-id="element-198">
                        Geldhaus AI Assistant
                      </h3>
                      <p className="text-primary-100 text-sm flex items-center gap-2" data-id="element-199">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-200"></span>
                        Online • Instant Response
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setIsExpanded(false)} className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors duration-200" aria-label="Minimize chat" data-id="element-201">
                    <ChevronUp size={24} data-id="element-202" />
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="h-96 overflow-y-auto p-6 space-y-4 bg-gray-50" data-id="element-203">
                {messages.map((message, index) => <div key={index} className={`flex gap-3 ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'} animate-fadeIn`} data-id="element-204">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${message.type === 'bot' ? 'bg-primary-100 text-primary-600' : 'bg-blue-100 text-blue-600'}`} data-id="element-205">
                      {message.type === 'bot' ? <Bot size={20} data-id="element-206" /> : <User size={20} data-id="element-207" />}
                    </div>
                    <div className={`max-w-[75%] p-4 rounded-2xl ${message.type === 'bot' ? 'bg-white text-gray-800 shadow-sm border border-gray-100' : 'bg-primary-600 text-white shadow-lg'}`} data-id="element-208">
                      <p className="text-sm leading-relaxed whitespace-pre-line" data-id="element-209">
                        {message.content}
                      </p>
                      <p className="text-xs opacity-60 mt-2" data-id="element-210">
                        {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </p>
                    </div>
                  </div>)}
                {isLoading && <div className="flex gap-3 animate-fadeIn" data-id="element-211">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-100 text-primary-600" data-id="element-212">
                      <Bot size={20} data-id="element-213" />
                    </div>
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100" data-id="element-214">
                      <div className="flex items-center gap-2" data-id="element-215">
                        <Loader2 size={16} className="animate-spin text-primary-600" data-id="element-216" />
                        <span className="text-sm text-gray-600" data-id="element-217">
                          Thinking...
                        </span>
                      </div>
                    </div>
                  </div>}
                <div ref={messagesEndRef} data-id="element-218" />
              </div>

              {/* Chat Input */}
              <div className="p-6 bg-white border-t border-gray-200" data-id="element-219">
                <div className="flex gap-3" data-id="element-220">
                  <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && !isLoading && handleSendMessage()} placeholder="Type your message here..." disabled={isLoading} className="flex-1 px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-gray-900 placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Chat message input" data-id="element-221" />
                  <button onClick={() => handleSendMessage()} disabled={isLoading || !inputValue.trim()} className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-xl transition-all duration-300 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Send message" data-id="element-222">
                    <span className="hidden sm:inline" data-id="element-223">Send</span>
                    {isLoading ? <Loader2 size={20} className="animate-spin" data-id="element-224" /> : <Send size={20} data-id="element-225" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-4 text-center" data-id="element-226">
                  🔒 Powered by AI • Your conversations are secure and
                  confidential
                </p>
              </div>
            </div>
          </div>}
      </div>
    </section>;
};